import type { Insight } from './insights';

export const karriereShopInsights: Insight[] = [
  // ───────────────────────── Onlineshop ─────────────────────────
  {
    slug: 'onlineshop-erstellen-checkliste',
    title: 'Onlineshop erstellen: Worauf Sie bei Shopsystem, Rechtssicherheit und Versand achten müssen',
    excerpt:
      'WooCommerce oder Shopify? Welche Pflichtangaben braucht ein Shop in Deutschland, wie automatisiert man DHL-Labels inklusive Alterssichtprüfung – und warum entscheiden Ladezeit und Bedienbarkeit über den Umsatz? Der Praxis-Ratgeber mit Checkliste.',
    seoTitle: 'Onlineshop erstellen: Checkliste Recht & Versand',
    seoDescription:
      'Onlineshop in Deutschland rechtssicher aufbauen: Shopsystem wählen, Pflichtangaben, Widerrufsbutton, DHL-Labels mit Alterssichtprüfung, Zahlungsarten, Ladezeit. Mit Checkliste & FAQ.',
    category: 'E-Commerce',
    readingTime: '13 Min.',
    datePublished: '2026-10-02',
    heroImage: '/images/ratgeber/onlineshop-checkliste',
    heroImageAlt: 'Shopbetreiberin verpackt eine Flasche für den Versand, daneben Laptop mit Shop-Übersicht und Etikettendrucker',
    author: { name: 'Jens Goldhammer', title: 'Technical Lead & KI-Experte' },
    tags: ['Onlineshop erstellen', 'WooCommerce', 'Shopify', 'Germanized', 'DHL Alterssichtprüfung', 'Rechtssicherer Shop', 'E-Commerce'],
    contentBlocks: [
      {
        type: 'lead',
        text: 'Ein Onlineshop ist schnell installiert – ein Shop, der rechtssicher verkauft, Bestellungen ohne Handarbeit abwickelt und Kunden nicht an der Kasse verliert, ist ein anderes Projekt. Die meisten Probleme entstehen nicht beim Design, sondern bei Details: fehlende Pflichtangaben, Versandlabels, die jemand von Hand abtippt, oder eine Produktseite, die auf dem Handy fünf Sekunden lädt.',
      },
      {
        type: 'paragraph',
        text: 'In diesem Ratgeber zeigen wir, worauf es beim Aufbau eines Shops in Deutschland ankommt: die Wahl des Shopsystems, die rechtlichen Pflichten, Zahlung und Versand, Geschwindigkeit und Bedienbarkeit. Als Praxisbeispiel dient der Shop von [Fly With Toni](/referenzen/flywithtoni), den wir für den Verkauf von Gin umgesetzt haben – ein Fall, in dem auch Altersprüfung und Lebensmittelangaben eine Rolle spielen.',
      },
      {
        type: 'callout',
        variant: 'info',
        title: 'Kernpunkte',
        text: 'Ein guter Shop braucht vier Dinge: ein passendes Shopsystem (WooCommerce oder Shopify), vollständige Pflichtangaben für den deutschen Markt, automatisierte Abläufe für Zahlung, Versand und E-Mails sowie schnelle, einfache Seiten – vor allem auf dem Smartphone. Wer Alkohol oder andere altersbeschränkte Waren verschickt, sollte die Altersprüfung direkt ins Versandlabel einbauen.',
      },

      { type: 'heading', text: 'Welches Shopsystem passt: WooCommerce oder Shopify?' },
      {
        type: 'paragraph',
        text: 'Beide Systeme sind ausgereift. Shopify ist inzwischen der bekannteste Einstieg ins Onlinegeschäft und weltweit verbreitet, WooCommerce läuft auf dem ebenso verbreiteten WordPress. Der Unterschied liegt weniger im Funktionsumfang als in der Frage, wie viel Kontrolle Sie haben möchten – und wer sich um Technik und Updates kümmert.',
      },
      {
        type: 'table',
        headers: ['Kriterium', 'WordPress + WooCommerce', 'Shopify'],
        rows: [
          ['Betrieb', 'Eigenes Hosting, volle Kontrolle', 'Gehostet, Technik übernimmt Shopify'],
          ['Laufende Kosten', 'Hosting + ggf. Premium-Plugins', 'Monatliche Gebühr + ggf. Apps'],
          ['Deutscher Markt', 'Sehr gut mit Germanized (Pro)', 'Gut mit deutschen Rechts-Apps'],
          ['Inhalte & Blog', 'Stark – WordPress ist ein vollwertiges CMS', 'Solide, aber weniger flexibel'],
          ['Pflegeaufwand', 'Updates und Sicherheit müssen betreut werden', 'Gering'],
          ['Warenwirtschaft & ERP', 'Möglich, wird aber individuell umgesetzt', 'Stark – viele fertige Anbindungen'],
          ['Ideal für', 'Kleine und mittlere Shops mit viel Inhalt, individuellen Abläufen, überschaubarem Budget', 'Größere Shops mit vielen Bestellungen und komplexen Anbindungen'],
        ],
        caption: 'Vereinfachter Vergleich – die richtige Wahl hängt vom Sortiment und Ihren Abläufen ab.',
      },
      {
        type: 'paragraph',
        text: 'Unsere Faustregel: Shopify ist sehr populär, hat aber einen Haken – die monatlichen Gebühren und App-Kosten laufen auch dann weiter, wenn ein Shop noch kaum Umsatz macht. Für einen kleinen Shop mit wenigen Bestellungen fallen sie deshalb überproportional ins Gewicht. Bei WooCommerce zahlen Sie vor allem Hosting und einmalige Umsetzung.',
      },
      {
        type: 'paragraph',
        text: 'Anders bei größerem Volumen: Wer täglich viele Bestellungen verarbeitet und Warenwirtschaft, ERP oder Steuerbuchhaltung komplett anbinden will, ist mit Shopify hervorragend beraten. Die Schnittstellen sind dort Standard, die Technik läuft wartungsfrei mit, und das Team muss sich nicht um Server und Updates kümmern. Kurz gesagt: kleiner Shop mit wenig Umsatz – WooCommerce. Größerer Shop mit vielen Anbindungen – Shopify.',
      },

      { type: 'heading', text: 'Rechtssicherheit: Welche Pflichtangaben braucht ein Onlineshop in Deutschland?' },
      {
        type: 'paragraph',
        text: 'Der deutsche Onlinehandel ist stark reguliert, und Abmahnungen treffen vor allem kleine Shops, die eine Pflichtangabe übersehen haben. Die gute Nachricht: Die meisten Anforderungen lassen sich einmal sauber einrichten und laufen dann automatisch mit.',
      },
      {
        type: 'list',
        items: [
          '**Impressum** nach § 5 DDG – von jeder Seite mit einem Klick erreichbar.',
          '**Datenschutzerklärung** – inklusive aller eingebundenen Dienste wie Zahlungsanbieter, Versanddienstleister und Analyse-Tools.',
          '**Widerrufsbelehrung und Muster-Widerrufsformular** – und seit dem 19. Juni 2026 ein gut sichtbarer elektronischer Widerrufsbutton. Mehr dazu in unserem Artikel zum [Widerrufsbutton](/ratgeber/pflicht-widerrufsbutton-2026).',
          '**AGB** – gesetzlich nicht vorgeschrieben, aber dringend empfohlen: Ohne sie gilt ausschließlich das Gesetz, und die Pflichtangaben zu Lieferung, Zahlung und Widerruf müssen Sie ohnehin erfüllen.',
          '**Preisangaben**: Endpreise inklusive Mehrwertsteuer, Hinweis auf Versandkosten und – bei Waren nach Gewicht oder Volumen – der Grundpreis, etwa pro Liter oder Kilogramm (Preisangabenverordnung).',
          '**Lieferzeit** – konkret und realistisch, nicht „in Kürze“.',
          '**Bestellbutton** mit eindeutiger Beschriftung wie „zahlungspflichtig bestellen“ (§ 312j BGB) und eine Übersicht aller wesentlichen Angaben direkt davor.',
          '**Cookie-Consent** nach § 25 TDDDG – Tracking und Marketing-Cookies erst nach ausdrücklicher Zustimmung.',
          '**Verpackungsregister LUCID** – wer verpackte Ware an Endkunden verschickt, muss sich vor dem ersten Verkauf registrieren und an einem Dualen System beteiligen.',
          '**Barrierefreiheit** – seit Juni 2025 gilt für viele Onlineshops das Barrierefreiheitsstärkungsgesetz. Ausgenommen sind Kleinstunternehmen. Details im Ratgeber [Barrierefreiheit & BFSG](/ratgeber/barrierefreiheit-bfsg-webseiten).',
        ],
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'Praxis-Tipp: Germanized Pro',
        text: 'Für WooCommerce-Shops richten wir die deutschen Pflichtangaben mit Germanized Pro ein: Grundpreise, Lieferzeiten, Mehrwertsteuer-Hinweise, Checkbox-Texte im Kassenbereich, rechtssichere E-Mails und Rechnungen. Die Rechtstexte selbst kommen von spezialisierten Anbietern wie eRecht24 und aktualisieren sich bei Gesetzesänderungen automatisch.',
      },
      {
        type: 'paragraph',
        text: 'Für bestimmte Produkte kommen Sonderregeln hinzu. Bei Lebensmitteln und Getränken müssen die Pflichtangaben der Lebensmittelinformationsverordnung (LMIV) bereits **vor** dem Kauf sichtbar sein – also Zutaten, Allergene, Alkoholgehalt, Füllmenge und Hersteller. Wer Elektrogeräte verkauft, braucht eine Registrierung nach ElektroG. Und bei Alkohol, Tabak oder anderen altersbeschränkten Waren muss sichergestellt sein, dass sie nicht an Minderjährige gehen.',
      },

      { type: 'heading', text: 'Altersbeschränkte Produkte: Wie funktioniert die Alterssichtprüfung beim Versand?' },
      {
        type: 'paragraph',
        text: 'Spirituosen wie Gin dürfen nach dem Jugendschutzgesetz nicht an Personen unter 18 Jahren abgegeben werden. Eine Checkbox „Ich bin über 18“ im Shop reicht dafür nicht aus – entscheidend ist, wer das Paket am Ende tatsächlich entgegennimmt.',
      },
      {
        type: 'paragraph',
        text: 'DHL bietet dafür den Zusatzservice **Alterssichtprüfung** (ab 16 oder ab 18 Jahren). Der Zusteller prüft bei der Übergabe das Alter des Empfängers und händigt das Paket nur aus, wenn das Mindestalter erreicht ist. Damit das im Alltag niemand vergisst, sollte der Service nicht manuell gebucht, sondern automatisch aufs Label kommen.',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'Im Produkt wird ein Mindestalter hinterlegt, zum Beispiel 18 Jahre für Gin.',
          'Enthält eine Bestellung ein solches Produkt, wird beim Erstellen des Labels automatisch die DHL-Alterssichtprüfung gebucht.',
          'Das Versandlabel wird über ein Plugin wie Shiptastic direkt aus der Bestellung erzeugt – Adresse, Gewicht und Services sind bereits befüllt.',
          'Die Sendungsnummer landet automatisch in der Versandbestätigung an den Kunden.',
        ],
      },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Achtung bei Packstation und Ablageort',
        text: 'Pakete mit Alterssichtprüfung können nicht an einem Ablageort oder beim Nachbarn abgegeben werden. Weisen Sie Ihre Kunden im Bestellprozess darauf hin – das vermeidet Rückläufer und Nachfragen.',
      },

      { type: 'heading', text: 'Zahlung, Versand und E-Mails: Was lässt sich automatisieren?' },
      {
        type: 'paragraph',
        text: 'Zeit kostet ein Shop vor allem dann, wenn nach jeder Bestellung Handarbeit anfällt: Zahlung prüfen, Label schreiben, Kunde informieren, Rechnung ablegen. Fast alles davon lässt sich automatisieren.',
      },
      {
        type: 'table',
        headers: ['Bereich', 'Was automatisch passieren sollte'],
        rows: [
          ['Zahlung', 'PayPal, Kreditkarte, Klarna oder Apple Pay – mit automatischer Statusänderung nach Zahlungseingang'],
          ['Versand', 'DHL-Label aus der Bestellung, inklusive Zusatzservices wie Alterssichtprüfung'],
          ['E-Mails', 'Bestell-, Zahlungs- und Versandbestätigung mit Sendungsnummer und Rechtstexten'],
          ['Rechnungen', 'Fortlaufend nummerierte PDF-Rechnungen, exportierbar für die Buchhaltung'],
          ['Buchhaltung', 'Export oder Schnittstelle zum Steuerberater, zum Beispiel per DATEV-Format'],
          ['Sicherheit', 'Firewall, Login-Schutz und Monitoring, etwa mit Wordfence bei WordPress'],
        ],
      },
      {
        type: 'paragraph',
        text: 'Bei Zahlungsarten gilt: Lieber wenige, aber die richtigen. In Deutschland sind PayPal und Kreditkarte Pflicht, Klarna oder Apple Pay sind für viele Zielgruppen ein Plus. Jede zusätzliche Zahlungsart ist eine weitere Gebühr und ein weiterer Prozess, den Sie verstehen sollten.',
      },

      { type: 'heading', text: 'Warum muss ein Onlineshop schnell sein?' },
      {
        type: 'paragraph',
        text: 'Ladezeit ist im E-Commerce kein Technik-Detail, sondern Umsatz. Jede Sekunde Wartezeit erhöht die Wahrscheinlichkeit, dass Besucher abspringen – besonders auf dem Smartphone und im mobilen Netz. Google bewertet die Geschwindigkeit zudem über die Core Web Vitals und berücksichtigt sie im Ranking.',
      },
      {
        type: 'list',
        items: [
          '**Bilder optimieren**: moderne Formate wie WebP, passende Größen für jedes Gerät, Lazy Loading unterhalb des sichtbaren Bereichs.',
          '**Wenige Plugins und Apps**: Jede Erweiterung bringt Code mit. Was nicht gebraucht wird, fliegt raus.',
          '**Gutes Hosting und Caching**: Bei WooCommerce entscheidet der Server stark über die Geschwindigkeit. Mehr dazu im Artikel [Hosting & Betrieb](/ratgeber/hosting-betrieb-schnelle-webseiten).',
          '**Zielwerte**: Largest Contentful Paint unter 2,5 Sekunden, Interaction to Next Paint unter 200 Millisekunden, kaum Layout-Verschiebungen (CLS unter 0,1).',
        ],
      },

      { type: 'heading', text: 'Was macht einen Shop intuitiv bedienbar?' },
      {
        type: 'paragraph',
        text: 'Ein intuitiver Shop fühlt sich an, als müsste man nicht nachdenken. Kunden finden das Produkt, verstehen den Preis und kommen ohne Umwege zur Kasse. Dafür braucht es kein ausgefallenes Design, sondern Klarheit.',
      },
      {
        type: 'list',
        items: [
          'Klare Navigation mit wenigen, verständlichen Kategorien.',
          'Produktseiten mit großen Bildern, kurzen Vorteilen, Preis, Lieferzeit und gut sichtbarem „In den Warenkorb“-Button.',
          'Gastbestellung ohne Kundenkonto-Zwang.',
          'Ein schlanker Checkout: so wenige Felder wie möglich, Adressprüfung, Zahlungsarten mit Logos.',
          'Vertrauenssignale: echte Kontaktmöglichkeit, Versandinfos, Bewertungen und ein sicheres Gefühl beim Bezahlen.',
          'Mobile first: Daumen-freundliche Buttons und Formulare, die auf dem Smartphone funktionieren.',
        ],
      },

      { type: 'heading', text: 'Praxisbeispiel: Wie wir den Fly-With-Toni-Shop umgesetzt haben' },
      {
        type: 'paragraph',
        text: 'Für Toni Eggert haben wir einen WooCommerce-Shop für seinen Take Off Gin umgesetzt. Die Bilder lieferte eine Berliner Agentur, wir übernahmen die komplette Umsetzung. Weil Gin ein alkoholisches Lebensmittel ist, waren die Anforderungen höher als bei einem gewöhnlichen Shop.',
      },
      {
        type: 'list',
        items: [
          'Germanized Pro für Grundpreis pro Liter, Mehrwertsteuer, Lieferzeit und Lebensmittelangaben.',
          'Automatisierte DHL-Versandlabels über Shiptastic – direkt aus der Bestellung.',
          'PayPal und Kreditkarte als Zahlungsarten, automatische Bestell- und Versandmails.',
          'Wordfence Security und Zugriffs-Monitoring für mehr Sicherheit.',
          'Shop-Anleitungen, damit der Kunde Produkte und Bestellungen selbst pflegen kann.',
          'Projektdauer: 7 Wochen von der Planung bis zum Livegang.',
        ],
      },
      {
        type: 'quote',
        text: 'Das Ziel war ein Shop, den der Kunde ohne Agentur betreiben kann – Bestellung rein, Label raus, Rechnung an den Steuerberater.',
        cite: 'Jens Goldhammer, Enno & Jens',
      },

      { type: 'heading', text: 'Checkliste: Ist Ihr Onlineshop startklar?' },
      {
        type: 'list',
        items: [
          'Shopsystem passt zu Sortiment, Budget und Pflegeaufwand.',
          'Impressum, Datenschutz, AGB, Widerrufsbelehrung und Widerrufsbutton sind vorhanden.',
          'Preise mit MwSt., Versandkosten, Grundpreis und Lieferzeit werden angezeigt.',
          'Der Bestellbutton heißt „zahlungspflichtig bestellen“.',
          'Cookie-Consent blockiert Tracking bis zur Zustimmung.',
          'LUCID-Registrierung und Dual-System-Vertrag sind erledigt.',
          'Produktspezifische Pflichtangaben (z. B. LMIV bei Lebensmitteln) sind sichtbar.',
          'Altersbeschränkte Produkte erhalten automatisch die DHL-Alterssichtprüfung.',
          'Zahlung, Labels, E-Mails und Rechnungen laufen automatisch.',
          'Mobile Ladezeit liegt im grünen Bereich der Core Web Vitals.',
          'Testbestellungen mit jeder Zahlungsart wurden durchgespielt.',
          'Backups, Updates und Sicherheits-Monitoring sind geregelt.',
        ],
      },
      {
        type: 'callout',
        variant: 'tip',
        text: 'Wenn Sie mehr als zwei Punkte nicht sicher abhaken können, lohnt sich ein kurzer Blick von außen – oft sind es Kleinigkeiten mit großer Wirkung.',
      },

      { type: 'heading', text: 'Warum Enno & Jens für Ihren Onlineshop?' },
      {
        type: 'list',
        items: [
          '**Beides aus einer Hand**: WooCommerce und Shopify – wir empfehlen, was zu Ihnen passt, nicht was wir lieber bauen.',
          '**Deutscher Markt im Blick**: Germanized Pro, Rechtstexte und Pflichtangaben richten wir von Anfang an ein.',
          '**Automatisierung statt Handarbeit**: Zahlung, DHL-Labels, E-Mails und Buchhaltungsexport.',
          '**Schnell und verständlich**: optimierte Bilder, schlanke Technik, klarer Checkout.',
          '**Selbstständig weiterarbeiten**: Shop-Anleitung und Einweisung inklusive.',
          '**Persönlich und regional**: feste Ansprechpartner aus Wernigerode im Harz.',
        ],
      },

      { type: 'heading', text: 'Fazit' },
      {
        type: 'paragraph',
        text: 'Ein Onlineshop ist dann erfolgreich, wenn er für Kunden einfach und für Sie als Betreiber entspannt ist. Das gelingt mit dem passenden Shopsystem, sauberen Pflichtangaben, automatisierten Abläufen und schnellen Seiten. Wer altersbeschränkte Produkte verkauft, baut die Altersprüfung direkt in den Versand ein – dann muss niemand mehr daran denken.',
      },

      { type: 'heading', text: 'Häufige Fragen zum Onlineshop' },
      {
        type: 'faq',
        items: [
          {
            question: 'Was kostet ein professioneller Onlineshop?',
            answer:
              'Das hängt von Sortiment, Zahl der Produkte, Zahlungs- und Versandanbindungen sowie individuellen Funktionen ab. Neben der einmaligen Umsetzung fallen laufende Kosten für Hosting oder Shopify-Gebühren, Plugins, Rechtstexte und Wartung an. Wir erstellen nach einem kurzen Gespräch ein transparentes Angebot.',
          },
          {
            question: 'Wie lange dauert es, einen Onlineshop aufzubauen?',
            answer:
              'Ein überschaubarer Shop ist in vier bis acht Wochen startklar. Der Fly-With-Toni-Shop ging nach sieben Wochen live – inklusive Rechtstexten, Versandautomatisierung und Shop-Anleitung. Den größten Einfluss auf die Dauer haben meist Produkttexte und Bilder.',
          },
          {
            question: 'Reicht eine Altersabfrage per Checkbox beim Verkauf von Alkohol?',
            answer:
              'Eine Checkbox ist ein Hinweis, aber keine verlässliche Prüfung. Sicherer ist die Alterssichtprüfung bei der Zustellung, zum Beispiel über den DHL-Zusatzservice. Der Zusteller kontrolliert das Alter bei der Übergabe, das Paket geht nur an volljährige Empfänger.',
          },
          {
            question: 'Brauche ich für meinen Shop AGB?',
            answer:
              'Nein. Keine Vorschrift schreibt die Verwendung von AGB vor, das entscheidet jeder Unternehmer selbst. Ohne AGB gilt aber ausschließlich das Gesetz, und Punkte wie Eigentumsvorbehalt, Lieferbedingungen oder Zahlungsfristen bleiben offen. Die Informationspflichten nach § 312d BGB und Art. 246a EGBGB – etwa zu Versandkosten, Zahlungsbedingungen und Widerruf – treffen Sie außerdem in jedem Fall, ob mit oder ohne AGB. In der Praxis werden sie deshalb meist über die AGB erfüllt. Wir empfehlen geprüfte AGB von einem spezialisierten Anbieter.',
          },
          {
            question: 'Kann ich meinen Shop nach dem Livegang selbst pflegen?',
            answer:
              'Ja. Wir bauen Shops so, dass Sie Produkte, Preise, Bestellungen und Kunden selbst verwalten können, und liefern auf Wunsch eine Shop-Anleitung und eine persönliche Einweisung mit. Für Updates, Sicherheit und Weiterentwicklungen stehen wir weiter zur Verfügung.',
          },
          {
            question: 'Welche Zahlungsarten sollte ein deutscher Onlineshop anbieten?',
            answer:
              'Mindestens PayPal und Kreditkarte. Je nach Zielgruppe ergänzen Klarna (Rechnung, Ratenkauf), Apple Pay oder Google Pay das Angebot sinnvoll. Wichtig ist, dass Zahlungsstatus und Bestellung automatisch zusammenspielen.',
          },
          {
            question: 'Ist WooCommerce oder Shopify besser für SEO?',
            answer:
              'Beide können sehr gut ranken. WooCommerce bietet durch WordPress mehr Freiheit für Inhalte, Ratgeber und technische Optimierungen. Bei Shopify ist die Technik einheitlicher, dafür sind manche Anpassungen eingeschränkt. Entscheidender als das System sind schnelle Seiten, gute Produkttexte und saubere Struktur.',
          },
        ],
      },
      {
        type: 'cta',
        title: 'Ihr Shop – rechtssicher und automatisiert',
        text: 'Sie planen einen Onlineshop oder wollen Ihren bestehenden Shop verbessern? Wir sprechen über Shopsystem, Pflichtangaben, Zahlung und Versand – unverbindlich und verständlich.',
        label: 'Zur Leistung E-Commerce-Shops',
        to: '/leistungen/onlineshop-erstellung',
      },
      {
        type: 'paragraph',
        text: 'Lieber direkt sprechen? [Unverbindlich Kontakt aufnehmen](/kontakt) – oder sehen Sie sich die [Fly-With-Toni-Referenz](/referenzen/flywithtoni) an.',
      },
      { type: 'heading', text: 'Rechtlicher Hinweis' },
      {
        type: 'callout',
        variant: 'info',
        text: 'Dieser Artikel dient der allgemeinen Information und stellt keine Rechtsberatung dar. Für die konkrete rechtliche Bewertung Ihres Shops sollte bei Bedarf eine qualifizierte Rechtsberatung hinzugezogen werden.',
      },
    ],
    sources: [
      { label: 'Preisangabenverordnung (PAngV)', url: 'https://www.gesetze-im-internet.de/pangv_2022/' },
      { label: '§ 312j BGB – Pflichten im elektronischen Geschäftsverkehr', url: 'https://www.gesetze-im-internet.de/bgb/__312j.html' },
      { label: 'Jugendschutzgesetz § 9 – Alkoholische Getränke', url: 'https://www.gesetze-im-internet.de/juschg/__9.html' },
      { label: 'Stiftung Zentrale Stelle Verpackungsregister – LUCID', url: 'https://www.verpackungsregister.org/' },
      { label: 'DHL Geschäftskunden – Zusatzservice Alterssichtprüfung', url: 'https://www.dhl.de/de/geschaeftskunden/paket/leistungen-und-services/services/service-loesungen/alterssichtpruefung.html' },
      { label: 'Google – Core Web Vitals', url: 'https://web.dev/articles/vitals' },
    ],
    relatedServices: ['onlineshop-erstellung', 'hosting-domains', 'webseitenerstellung'],
  },

  // ───────────────────────── Karriereseite ─────────────────────────
  {
    slug: 'karriereseite-benefits-bewerbung',
    title: 'Karriereseite: Warum sie über Bewerbungen entscheidet – und wie Sie Benefits richtig zeigen',
    excerpt:
      'Fachkräfte schauen sich Ihre Karriereseite an, bevor sie sich bewerben. Was eine gute Karriereseite ausmacht, wie Sie Benefits glaubwürdig darstellen und warum der Weg zur Bewerbung kurz sein muss – mit Praxisbeispiel Mundwerk Logopädie, Checkliste und FAQ.',
    seoTitle: 'Karriereseite erstellen: Benefits & Bewerbung',
    seoDescription:
      'Wie eine Karriereseite mehr passende Bewerbungen bringt: Arbeitgebermarke, Benefits, Team-Einblicke, Bewerbung in 2 Minuten, Google for Jobs. Mit Beispiel, Checkliste & FAQ.',
    category: 'Karriereseiten',
    readingTime: '12 Min.',
    datePublished: '2026-09-01',
    heroImage: '/images/ratgeber/karriereseite-benefits-bewerbung',
    heroImageAlt: 'Junge Logopädin in einer hellen Praxis sieht sich auf dem Smartphone eine Karriereseite an',
    author: { name: 'Enrico Kerwel', title: 'Creative Director' },
    tags: ['Karriereseite', 'Employer Branding', 'Benefits', 'Bewerber-Funnel', 'Fachkräftemangel', 'Recruiting Harz'],
    contentBlocks: [
      {
        type: 'lead',
        text: 'Ob Logopädin, Elektriker oder Pflegekraft: Gute Fachkräfte können sich ihren Arbeitgeber heute aussuchen. Bevor sie sich bewerben, googeln sie – und landen auf Ihrer Webseite. Was sie dort finden, entscheidet, ob aus Interesse eine Bewerbung wird oder ob sie zum nächsten Betrieb weiterklicken.',
      },
      {
        type: 'paragraph',
        text: 'Eine Stellenanzeige im Jobportal erzählt, was Sie suchen. Eine Karriereseite erzählt, warum man bei Ihnen arbeiten möchte. In diesem Ratgeber zeigen wir, was eine überzeugende Karriereseite ausmacht, wie Sie Benefits glaubwürdig hervorheben und wie Sie den Weg zur Bewerbung so leicht machen, dass er in zwei Minuten erledigt ist – am Beispiel der Praxis [Mundwerk Logopädie](/referenzen/mundwerk-logopaedie) aus Wernigerode und Quedlinburg.',
      },
      {
        type: 'callout',
        variant: 'info',
        title: 'Kernpunkte',
        text: 'Eine gute Karriereseite zeigt echte Menschen statt Stockfotos, benennt Benefits konkret statt allgemein und macht die Bewerbung so einfach wie möglich – idealerweise in wenigen Klicks vom Smartphone, ohne Anschreiben-Pflicht. Dazu kommt Sichtbarkeit: Stellen sollten bei Google und in der Region gefunden werden.',
      },

      { type: 'heading', text: 'Warum ist eine eigene Karriereseite so wichtig?' },
      {
        type: 'paragraph',
        text: 'Jobportale sind gut, um Reichweite zu erzeugen. Aber dort steht Ihre Anzeige zwischen hunderten anderen – im gleichen Layout, mit den gleichen Floskeln. Interessierte Bewerber klicken deshalb fast immer weiter auf Ihre eigene Webseite, um herauszufinden, wie es bei Ihnen wirklich ist.',
      },
      {
        type: 'list',
        items: [
          '**Vertrauen**: Bewerber wollen sehen, mit wem sie arbeiten und wie der Alltag aussieht.',
          '**Unterscheidung**: Gerade kleine und regionale Betriebe können mit Nähe, Teamgröße und Flexibilität punkten – wenn sie es zeigen.',
          '**Unabhängigkeit**: Eine eigene Karriereseite arbeitet rund um die Uhr, ohne laufende Anzeigenkosten.',
          '**Bessere Passung**: Wer vorher ehrlich informiert ist, bewirbt sich gezielter – das spart Zeit in Gesprächen.',
        ],
      },

      { type: 'heading', text: 'Was gehört auf eine gute Karriereseite?' },
      {
        type: 'table',
        headers: ['Baustein', 'Wozu er dient'],
        rows: [
          ['Kurzes Arbeitgeber-Versprechen', 'In einem Satz: Warum bei uns?'],
          ['Offene Stellen', 'Übersichtlich, mit Ort, Umfang und Startdatum'],
          ['Benefits', 'Konkrete Vorteile, die Bewerber sofort einordnen können'],
          ['Team & Alltag', 'Fotos, Videos und Stimmen aus dem Team'],
          ['Ablauf der Bewerbung', 'Was passiert nach dem Absenden? Wie schnell melden Sie sich?'],
          ['Einfache Bewerbung', 'Kurzer Funnel oder Formular, mobil nutzbar'],
          ['Ansprechperson', 'Name, Foto und direkter Kontakt für Rückfragen'],
        ],
      },

      { type: 'heading', text: 'Wie stellt man Benefits glaubwürdig dar?' },
      {
        type: 'paragraph',
        text: '„Flache Hierarchien“, „familiäres Umfeld“ und „attraktive Vergütung“ stehen in fast jeder Anzeige – und sagen deshalb fast nichts. Benefits wirken erst, wenn sie konkret, überprüfbar und für die Zielgruppe relevant sind.',
      },
      {
        type: 'table',
        headers: ['Austauschbar', 'Konkret und glaubwürdig'],
        rows: [
          ['Flexible Arbeitszeiten', 'Sie planen Ihre Termine selbst – auch 4-Tage-Woche möglich'],
          ['Weiterbildung', 'Fortbildungsbudget pro Jahr plus freie Tage dafür'],
          ['Gute Einarbeitung', 'Feste Mentorin in den ersten drei Monaten'],
          ['Modernes Arbeitsumfeld', 'Eigener Therapieraum, aktuelle Materialien, digitale Dokumentation'],
          ['Attraktive Vergütung', 'Gehaltsspanne direkt in der Stellenanzeige'],
        ],
        caption: 'Beispiele – verwenden Sie nur Benefits, die es bei Ihnen wirklich gibt.',
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'Gehalt zeigen',
        text: 'Immer mehr Bewerber erwarten eine Gehaltsangabe. Die EU-Entgelttransparenzrichtlinie verlangt künftig ohnehin, dass Bewerber vorab Informationen zum Einstiegsgehalt oder zur Gehaltsspanne erhalten. Wer die Spanne schon jetzt nennt, bekommt passendere Bewerbungen.',
      },
      {
        type: 'paragraph',
        text: 'Weniger ist dabei mehr: Fünf bis acht starke Benefits mit Icon und kurzem Satz bleiben besser hängen als eine Liste mit zwanzig Punkten. Und sie sollten auf der Karriereseite **oben** stehen, nicht versteckt am Ende.',
      },

      { type: 'heading', text: 'Warum muss der Weg zur Bewerbung so leicht sein?' },
      {
        type: 'paragraph',
        text: 'Viele Fachkräfte suchen nicht aktiv, sondern stoßen abends auf dem Sofa per Smartphone auf eine Stelle. Wer dann ein Anschreiben formulieren, einen Lebenslauf als PDF hochladen und zehn Pflichtfelder ausfüllen muss, schiebt die Bewerbung auf – und meistens bleibt es dabei.',
      },
      {
        type: 'paragraph',
        text: 'Ein **Bewerber-Funnel** löst das: Statt eines langen Formulars beantworten Interessierte nacheinander wenige, einfache Fragen per Klick – etwa zur Qualifikation, zum gewünschten Stundenumfang und zum Startzeitpunkt. Am Ende stehen Name und Telefonnummer. Den Lebenslauf kann man später nachreichen.',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'Welche Stelle interessiert Sie? (Auswahl per Klick)',
          'Welche Qualifikation bringen Sie mit?',
          'Voll- oder Teilzeit – und ab wann?',
          'Wie dürfen wir Sie erreichen? (Name, Telefon, E-Mail)',
          'Fertig – mit Bestätigung, wie es weitergeht.',
        ],
      },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Datenschutz nicht vergessen',
        text: 'Bewerberdaten sind besonders schützenswert. Das Formular braucht eine verschlüsselte Übertragung, eine Datenschutzerklärung mit Hinweis auf die Bewerberdaten und eine klare Löschfrist, wenn es nicht zu einer Einstellung kommt. Stellentitel sollten neutral formuliert sein, etwa mit (m/w/d).',
      },

      { type: 'heading', text: 'Praxisbeispiel: Die Karriereseite von Mundwerk Logopädie' },
      {
        type: 'paragraph',
        text: 'Die Praxis Mundwerk – Logopädie und Schluckambulanz in Wernigerode und Quedlinburg – stand vor einer typischen Herausforderung: Logopädinnen und Logopäden sind gesucht, die alte Webseite zeigte aber kaum, wie es im Team wirklich aussieht. Gemeinsam haben wir den Auftritt neu aufgebaut und die Karriereseite in den Mittelpunkt gerückt.',
      },
      {
        type: 'list',
        items: [
          '**Echte Einblicke**: Hintergrundvideo und Praxisfotos, die wir in das Design eingebunden haben, zeigen Räume und Team.',
          '**Stimmen aus dem Team**: Mitarbeitende erzählen, warum sie gerne bei Mundwerk arbeiten.',
          '**Benefits auf einen Blick**: Die wichtigsten Vorteile stehen direkt oben – kurz und verständlich.',
          '**Bewerbung in 2 Minuten**: Ein mehrstufiger Bewerber-Funnel ersetzt das lange Formular.',
          '**Regional sichtbar**: Local SEO für Wernigerode und Quedlinburg, damit Fachkräfte aus der Region die Praxis finden.',
          '**Instagram ohne Tracking**: Der Feed ist eingebunden, ohne beim Seitenaufruf Daten an Instagram zu übertragen.',
        ],
      },
      {
        type: 'quote',
        text: 'Eine Karriereseite muss sich anfühlen wie ein Besuch in der Praxis – nur eben vom Sofa aus.',
        cite: 'Enrico Kerwel, Enno & Jens',
      },

      { type: 'heading', text: 'Wie wird die Karriereseite bei Google gefunden?' },
      {
        type: 'list',
        items: [
          '**Google for Jobs**: Mit strukturierten Daten (JobPosting) erscheinen Ihre Stellen direkt in der Google-Jobsuche – kostenlos.',
          '**Regionale Begriffe**: „Logopäde Jobs Wernigerode“ oder „Ausbildung Harz“ gehören in Titel und Texte, ohne künstlich zu wirken. Mehr dazu im Ratgeber [Local SEO im Harz](/ratgeber/local-seo-harz-wernigerode).',
          '**Eine Seite pro Stelle**: Jede Stelle bekommt eine eigene Unterseite mit eindeutigem Titel.',
          '**Schnell und mobil**: Langsame Seiten verlieren Bewerber und Rankings.',
        ],
      },

      { type: 'heading', text: 'Checkliste: Ist Ihre Karriereseite bewerberfreundlich?' },
      {
        type: 'list',
        items: [
          'In einem Satz steht oben, warum man bei Ihnen arbeiten sollte.',
          'Offene Stellen sind mit Ort, Umfang und Startdatum aufgelistet.',
          'Fünf bis acht konkrete Benefits sind gut sichtbar.',
          'Echte Fotos oder Videos von Team und Arbeitsplatz.',
          'Mindestens zwei Stimmen aus dem Team.',
          'Gehaltsspanne oder Gehaltsinformation ist angegeben.',
          'Bewerbung per Smartphone in wenigen Minuten möglich, ohne Anschreiben-Pflicht.',
          'Der Ablauf nach der Bewerbung ist erklärt.',
          'Eine Ansprechperson mit Name und Kontakt ist genannt.',
          'Datenschutzhinweise und Löschfrist für Bewerberdaten sind vorhanden.',
          'Stellen sind für Google for Jobs ausgezeichnet.',
          'Die Seite lädt auch im Mobilnetz schnell.',
        ],
      },
      {
        type: 'callout',
        variant: 'tip',
        text: 'Machen Sie den Test: Bewerben Sie sich selbst einmal auf Ihre eigene Stelle – mit dem Smartphone. Wie lange dauert es, und wo würden Sie aufgeben?',
      },

      { type: 'heading', text: 'Warum Enno & Jens für Ihre Karriereseite?' },
      {
        type: 'list',
        items: [
          '**Arbeitgebermarke zuerst**: Wir klären gemeinsam, was Sie als Arbeitgeber wirklich auszeichnet.',
          '**Bewerber-Funnels**, die die Hürde zur Bewerbung deutlich senken.',
          '**Echte Inhalte**: Wir binden Ihre Fotos und Videos so ein, dass sie wirken.',
          '**Gefunden werden**: Local SEO und Google for Jobs inklusive.',
          '**Datenschutzfreundlich**: Formulare und Einbindungen ohne unnötiges Tracking.',
          '**Aus der Region**: Wir kennen den Fachkräftemarkt im Harz.',
        ],
      },

      { type: 'heading', text: 'Fazit' },
      {
        type: 'paragraph',
        text: 'Im Wettbewerb um Fachkräfte gewinnt nicht der Betrieb mit der lautesten Anzeige, sondern der, bei dem man sich vorher schon vorstellen kann, dort zu arbeiten. Eine Karriereseite mit echten Einblicken, konkreten Benefits und einem kurzen Weg zur Bewerbung ist dafür das wirkungsvollste Werkzeug – und sie arbeitet jeden Tag für Sie.',
      },

      { type: 'heading', text: 'Häufige Fragen zur Karriereseite' },
      {
        type: 'faq',
        items: [
          {
            question: 'Lohnt sich eine Karriereseite auch für kleine Betriebe?',
            answer:
              'Gerade für kleine Betriebe. Sie können mit Nähe, Teamgröße und Flexibilität punkten, die große Unternehmen nicht bieten – müssen das aber sichtbar machen. Schon eine einzige gut gemachte Karriereseite mit Benefits und einfacher Bewerbung macht oft einen spürbaren Unterschied.',
          },
          {
            question: 'Was ist ein Bewerber-Funnel?',
            answer:
              'Ein Bewerber-Funnel ist eine Bewerbung in mehreren kleinen Schritten: Interessierte beantworten wenige Fragen per Klick und hinterlassen am Ende ihre Kontaktdaten. Das geht in rund zwei Minuten am Smartphone und senkt die Hürde gegenüber einem klassischen Formular mit Anschreiben deutlich.',
          },
          {
            question: 'Welche Benefits sollte ich auf der Karriereseite zeigen?',
            answer:
              'Die, die es wirklich gibt und die Ihrer Zielgruppe wichtig sind – zum Beispiel flexible Arbeitszeiten, Weiterbildungsbudget, Einarbeitung, Ausstattung oder Gehaltsspanne. Formulieren Sie konkret: statt „Weiterbildung“ lieber „Fortbildungsbudget und freie Tage dafür“.',
          },
          {
            question: 'Brauche ich professionelle Fotos und Videos?',
            answer:
              'Echte Bilder sind wichtiger als perfekte. Authentische Aufnahmen aus Ihrem Alltag wirken stärker als Stockfotos. Wenn bereits Fotos oder Videos vorhanden sind, binden wir sie passend ein – so wie bei Mundwerk Logopädie.',
          },
          {
            question: 'Wie erscheinen meine Stellen bei Google for Jobs?',
            answer:
              'Jede Stelle wird auf einer eigenen Seite mit strukturierten Daten vom Typ JobPosting ausgezeichnet – mit Titel, Ort, Beschäftigungsart und Datum. Google liest diese Informationen aus und zeigt die Stelle in der Jobsuche an. Dafür fallen keine Anzeigenkosten an.',
          },
          {
            question: 'Muss ich das Gehalt in der Stellenanzeige angeben?',
            answer:
              'Bisher ist das in Deutschland nicht generell Pflicht. Die EU-Entgelttransparenzrichtlinie sieht aber vor, dass Bewerber künftig vorab Informationen zum Einstiegsgehalt oder zur Spanne erhalten. Viele Betriebe nennen die Spanne schon jetzt, weil sie dadurch passendere Bewerbungen bekommen.',
          },
          {
            question: 'Wie lange dauert es, eine Karriereseite zu erstellen?',
            answer:
              'Eine Karriereseite als Teil einer Webseite ist meist in zwei bis sechs Wochen umgesetzt. Am längsten dauert erfahrungsgemäß das Sammeln von Inhalten wie Team-Stimmen, Benefits und Fotos – dabei unterstützen wir Sie.',
          },
        ],
      },
      {
        type: 'cta',
        title: 'Mehr passende Bewerbungen gewinnen',
        text: 'Sie suchen Fachkräfte und möchten, dass Bewerber Sie von Ihrer besten Seite kennenlernen? Wir zeigen Ihnen, wie eine Karriereseite mit Bewerber-Funnel für Ihren Betrieb aussehen kann.',
        label: 'Zur Leistung Karriereseiten',
        to: '/leistungen/karriereseiten',
      },
      {
        type: 'paragraph',
        text: 'Lieber direkt sprechen? [Unverbindlich Kontakt aufnehmen](/kontakt) – oder sehen Sie sich die [Mundwerk-Referenz](/referenzen/mundwerk-logopaedie) an.',
      },
    ],
    sources: [
      { label: 'Richtlinie (EU) 2023/970 zur Entgelttransparenz', url: 'https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32023L0970' },
      { label: 'Google Search Central – Strukturierte Daten für Stellenanzeigen (JobPosting)', url: 'https://developers.google.com/search/docs/appearance/structured-data/job-posting?hl=de' },
      { label: 'Allgemeines Gleichbehandlungsgesetz (AGG)', url: 'https://www.gesetze-im-internet.de/agg/' },
    ],
    relatedServices: ['karriereseiten', 'webseitenerstellung', 'seo-optimierung'],
  },
];
